import React, { useState, useEffect } from 'react';
import { generateContentCalendar, generatePostImage } from './services/geminiService';
import { overlayLogoOnImage } from './services/imageUtils';
import InputForm from './components/InputForm';
import PostCard from './components/PostCard';
import PublishModal from './components/PublishModal';
import LandingPage from './components/LandingPage';
import AuthForm from './components/AuthForm';
import PricingPage from './components/PricingPage';
import PaymentStatus from './components/PaymentStatus';
import AdminDashboard from './components/AdminDashboard';
import PaywallModal from './components/PaywallModal';
import LowCreditsAlert from './components/LowCreditsAlert';
import ProfileSettings from './components/ProfileSettings';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Contact from './components/Contact';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import TeamSettings from './components/TeamSettings';
import TeamInvite from './components/TeamInvite';
import CalendarHistory from './components/CalendarHistory';
import SocialQueue from './components/SocialQueue';
import ConnectedAccounts from './components/ConnectedAccounts';
import { useAuth } from './contexts/AuthContext';
import { paymentsApi } from './services/paymentsApi';
import { calendarsApi } from './services/calendarsApi';
import { v4 as uuidv4 } from 'uuid';
import { 
  Rocket, Sparkles, RotateCcw, Image as ImageIcon, Loader2, Newspaper, Info, LogOut, User as UserIcon,
  CreditCard, Shield, AlertTriangle, Settings, BarChart3, Users, History, Send, Link2
} from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import './App.css';

const ADMIN_EMAIL = 'reggie@coolgeek.me';

// Free tier allows 1 free generation for new users
const FREE_TIER_CALENDARS = 1;

function App() {
  const { user, logout } = useAuth();
  const [view, setView] = useState('landing'); // 'landing' | 'auth' | 'input' | 'results' | 'payment-success' | 'payment-cancel'
  const [isLoading, setIsLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [isBatchGenerating, setIsBatchGenerating] = useState(false);
  const [newsSummary, setNewsSummary] = useState(undefined);
  const [newsCitations, setNewsCitations] = useState([]);
  
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Pricing and Admin modals
  const [showPricing, setShowPricing] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  
  // Feature gating
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('');
  const [paywallCredits, setPaywallCredits] = useState(null);
  const [freeGenerationsUsed, setFreeGenerationsUsed] = useState(() => {
    const stored = localStorage.getItem('flowgen_free_generations_used');
    return stored ? parseInt(stored, 10) : 0;
  });
  
  // Profile settings
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  
  // Legal pages
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showContact, setShowContact] = useState(false);
  
  // Analytics and Teams
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const [showTeamInvite, setShowTeamInvite] = useState(false);
  
  // Calendar History
  const [showCalendarHistory, setShowCalendarHistory] = useState(false);
  const [currentCalendarId, setCurrentCalendarId] = useState(null);
  
  // Active Team for saving calendars
  const [userTeams, setUserTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(null);
  
  // Automation settings
  const [automationSettings, setAutomationSettings] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  
  // Post filter for results view
  const [postFilter, setPostFilter] = useState('all'); // 'all' | 'draft' | 'scheduled' | 'posted'
  
  // Connected platforms state
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [showConnectPlatforms, setShowConnectPlatforms] = useState(false);

  // Check URL for payment status and team invites
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    
    if (path === '/payment/success') {
      setView('payment-success');
    } else if (path === '/payment/cancel') {
      setView('payment-cancel');
    } else if (path === '/team/invite' && params.get('token')) {
      setShowTeamInvite(true);
    }
  }, []);

  // Load user credits
  useEffect(() => {
    if (user?.uid) {
      loadUserCredits();
      loadUserTeams();
      loadAutomationSettings();
      loadConnectedAccounts();
    }
  }, [user]);

  const loadAutomationSettings = async () => {
    if (user?.uid) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/automation/settings/${user.uid}`);
        if (response.ok) {
          const settings = await response.json();
          setAutomationSettings(settings);
        }
      } catch (err) {
        console.error('Failed to load automation settings:', err);
      }
    }
  };

  const loadConnectedAccounts = async () => {
    if (user?.uid) {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/social/connected-accounts?user_id=${user.uid}`);
        if (response.ok) {
          const data = await response.json();
          setConnectedAccounts(data.accounts || []);
        }
      } catch (err) {
        console.error('Failed to load connected accounts:', err);
      }
    }
  };

  const loadUserCredits = async () => {
    if (user?.uid) {
      try {
        const credits = await paymentsApi.getUserCredits(user.uid, user.email);
        setUserCredits(credits);
      } catch (error) {
        console.error('Error loading credits:', error);
      }
    }
  };

  const loadUserTeams = async () => {
    if (user?.uid) {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/teams/user/${user.uid}`);
        if (response.ok) {
          const data = await response.json();
          setUserTeams(data.teams || []);
        }
      } catch (error) {
        console.error('Error loading teams:', error);
      }
    }
  };

  // Redirect to Input if user logs in while on Auth or Landing
  useEffect(() => {
    if (user && (view === 'auth' || view === 'landing')) {
      setView('input');
    }
  }, [user, view]);

  const handleStart = () => {
    if (user) {
      setView('input');
    } else {
      setView('auth');
    }
  };

  const handleLogout = async () => {
    await logout();
    setView('landing');
    setPosts([]);
    setCurrentProfile(null);
  };

  // Check if user can generate content (has credits or free tier)
  const canGenerateContent = () => {
    // Admin always has access
    if (user?.email === ADMIN_EMAIL) return true;
    
    // Check if user has paid credits
    if (userCredits) {
      // Has unlimited calendars
      if (userCredits.calendars_remaining === -1) return true;
      // Has remaining paid calendars
      if (userCredits.calendars_remaining > 0) return true;
      // Has an active subscription
      if (userCredits.subscription_active) return true;
    }
    
    // Check free tier (1 free generation per user)
    if (freeGenerationsUsed < FREE_TIER_CALENDARS) return true;
    
    return false;
  };

  const handleGenerate = async (profile) => {
    // Feature gating check
    if (!canGenerateContent()) {
      setPaywallFeature('Content Generation');
      setPaywallCredits('calendar credits');
      setShowPaywall(true);
      return;
    }
    
    setIsLoading(true);
    setCurrentProfile(profile);
    setCurrentCalendarId(null); // Reset calendar ID for new generation
    
    try {
      const result = await generateContentCalendar(profile);
      console.log('Generation result:', result);
      console.log('Citations received:', result.newsCitations);
      setNewsSummary(result.newsSummary);
      setNewsCitations(result.newsCitations || []);
      
      if (result.posts && Array.isArray(result.posts) && result.posts.length > 0) {
        const newPosts = result.posts.map((p) => ({
          id: uuidv4(),
          ...p,
          hashtags: p.hashtags || [],
          status: 'draft',
          platform: p.platform
        }));
        setPosts(newPosts);
        setView('results');
        
        // Auto-save the calendar to backend
        if (user?.uid) {
          try {
            const platforms = [...new Set(newPosts.map(p => p.platform).filter(Boolean))];
            const savedCalendar = await calendarsApi.saveCalendar({
              user_id: user.uid,
              user_email: user.email,
              user_name: user.displayName,
              team_id: activeTeamId, // Include team if selected
              profile_id: profile.id || uuidv4(),
              profile_name: profile.name || 'Unnamed Profile',
              posts: newPosts,
              news_summary: result.newsSummary,
              platforms: platforms,
              days: profile.days || 7,
            });
            setCurrentCalendarId(savedCalendar.calendar_id || savedCalendar.id);
            console.log('Calendar auto-saved:', savedCalendar, activeTeamId ? `(Team: ${activeTeamId})` : '(Personal)');
          } catch (err) {
            console.error('Failed to auto-save calendar:', err);
          }
        }
        
        // Deduct credit after successful generation
        if (user?.uid && userCredits && userCredits.calendars_remaining !== -1 && userCredits.calendars_remaining > 0) {
          // User has paid credits - deduct from backend
          try {
            await paymentsApi.useCalendarCredit(user.uid);
            loadUserCredits(); // Refresh credits
          } catch (err) {
            console.error('Failed to deduct credit:', err);
          }
        } else if (freeGenerationsUsed < FREE_TIER_CALENDARS) {
          // Using free tier
          const newCount = freeGenerationsUsed + 1;
          setFreeGenerationsUsed(newCount);
          localStorage.setItem('flowgen_free_generations_used', newCount.toString());
        }
      } else {
        alert("The AI generated a response, but it didn't contain any valid posts.");
      }
    } catch (error) {
      console.error('Content generation error:', error);
      const errorMessage = error.message || 'Unknown error';
      
      // Check for specific error types
      if (error.name === 'TypeError' && errorMessage.includes('Failed to fetch')) {
        alert("Network error: Could not connect to the server. This may be a CORS issue. Please try again or contact support.");
      } else if (errorMessage.includes('Budget') || errorMessage.includes('budget')) {
        alert("AI generation budget exceeded. Please contact support or try again later.");
      } else if (errorMessage.includes('Server error')) {
        alert(`Server error: ${errorMessage}. Please try again.`);
      } else if (errorMessage.includes('network') || errorMessage.includes('Network')) {
        alert("Network error. Please check your connection and try again.");
      } else if (errorMessage.includes('CORS') || errorMessage.includes('cors')) {
        alert("Connection blocked. Please contact support - there may be a server configuration issue.");
      } else {
        alert(`Content generation failed: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePost = (updatedPost) => {
    setPosts(prev => {
      const newPosts = prev.map(p => p.id === updatedPost.id ? updatedPost : p);
      
      // Sync with backend if we have a calendar ID
      if (currentCalendarId && user?.uid) {
        calendarsApi.updateCalendar(currentCalendarId, user.uid, { posts: newPosts })
          .catch(err => console.error('Failed to sync post update:', err));
      }
      
      return newPosts;
    });
  };

  const handlePublishClick = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handleConfirmPosted = (postUrl = null) => {
    if (selectedPost) {
      handleUpdatePost({ 
        ...selectedPost, 
        status: 'posted',
        posted_at: new Date().toISOString(),  // Track when it was posted
        post_url: postUrl  // Store the URL to the actual post
      });
    }
    setIsModalOpen(false);
    setTimeout(() => setSelectedPost(null), 200);
  };

  // Load a calendar from history
  const handleLoadCalendar = (calendar) => {
    // Restore calendar data
    setPosts(calendar.posts || []);
    setNewsSummary(calendar.news_summary);
    setCurrentCalendarId(calendar.id);
    setCurrentProfile({
      id: calendar.profile_id,
      name: calendar.profile_name,
    });
    setView('results');
  };

  const resetApp = () => {
    setPosts([]);
    setNewsSummary(undefined);
    setCurrentCalendarId(null);
    setView('input');
  };

  // Check if user can generate images
  const canGenerateImages = () => {
    // Admin always has access
    if (user?.email === ADMIN_EMAIL) return true;
    
    // Check if user has image regen credits
    if (userCredits) {
      if (userCredits.image_regens_remaining === -1) return true;
      if (userCredits.image_regens_remaining > 0) return true;
      if (userCredits.subscription_active) return true;
    }
    
    // Free tier includes limited image generations
    return freeGenerationsUsed <= FREE_TIER_CALENDARS;
  };

  const generateImagesForPosts = async (targetPosts) => {
    // Feature gating check for images
    if (!canGenerateImages()) {
      setPaywallFeature('AI Image Generation');
      setPaywallCredits('image regeneration credits');
      setShowPaywall(true);
      return;
    }
    
    const queue = targetPosts.filter(p => !p.imageUrl && p.imagePrompt);
    if (queue.length === 0) return;
    setIsBatchGenerating(true);
    setPosts(prev => prev.map(p => queue.find(q => q.id === p.id) ? { ...p, isGeneratingImage: true } : p));

    for (const post of queue) {
      try {
        let url = await generatePostImage(post.imagePrompt);
        if (currentProfile?.logo) {
          try {
            url = await overlayLogoOnImage(url, currentProfile.logo);
          } catch (e) { console.error(e); }
        }
        setPosts(prev => prev.map(p => p.id === post.id ? { ...p, imageUrl: url, isGeneratingImage: false } : p));
        
        // Deduct image regen credit
        if (user?.uid && userCredits && userCredits.image_regens_remaining > 0) {
          try {
            await paymentsApi.useImageRegen(user.uid);
          } catch (err) {
            console.error('Failed to deduct image credit:', err);
          }
        }
      } catch (error) {
        setPosts(prev => prev.map(p => p.id === post.id ? { ...p, isGeneratingImage: false } : p));
      }
    }
    
    // Refresh credits after batch generation
    if (user?.uid) {
      loadUserCredits();
    }
    setIsBatchGenerating(false);
  };

  // Filter posts based on status
  const filteredPosts = posts.filter(post => {
    if (postFilter === 'all') return true;
    return post.status === postFilter;
  });

  const postsByDate = filteredPosts.reduce((acc, post) => {
    if (!acc[post.date]) acc[post.date] = [];
    acc[post.date].push(post);
    return acc;
  }, {});

  const sortedDates = Object.keys(postsByDate).sort();
  
  // Post counts for tabs
  const postCounts = {
    all: posts.length,
    draft: posts.filter(p => p.status === 'draft').length,
    scheduled: posts.filter(p => p.status === 'scheduled').length,
    posted: posts.filter(p => p.status === 'posted').length
  };

  if (view === 'landing') {
    return (
      <>
        <LandingPage 
          onStart={handleStart}
          onShowPrivacy={() => setShowPrivacy(true)}
          onShowTerms={() => setShowTerms(true)}
          onShowContact={() => setShowContact(true)}
        />
        {/* Legal Pages accessible from landing */}
        {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
        {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
        {showContact && <Contact onClose={() => setShowContact(false)} />}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-body flex flex-col">
      <header className="border-b border-brand-gray-800 bg-black/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setView('landing')}>
            <div className="bg-brand-orange p-2 rounded-lg group-hover:bg-brand-orange/90 transition-colors"><Rocket className="w-5 h-5 text-black" /></div>
            <h1 className="text-xl font-headline uppercase tracking-wider text-white">FlowGen.social</h1>
          </div>
          
          <div className="flex items-center gap-3">
            {user && (
              <div className="flex items-center gap-4 mr-4 border-r border-brand-gray-800 pr-4">
                <button 
                  onClick={() => setShowProfileSettings(true)}
                  className="flex items-center gap-2 text-sm text-brand-gray-300 hover:text-white transition-colors group"
                  data-testid="profile-settings-btn"
                  title="Profile Settings"
                >
                  <UserIcon className="w-4 h-4 text-brand-orange" />
                  <span className="hidden sm:inline">{user.displayName || user.email}</span>
                  <Settings className="w-3 h-3 text-brand-gray-500 group-hover:text-white hidden sm:inline" />
                </button>
                {/* Credits display */}
                <div className="hidden md:flex items-center gap-2 text-xs">
                  {userCredits && userCredits.subscription_active ? (
                    <span className="px-2 py-0.5 bg-brand-orange/20 text-brand-orange border border-brand-orange/30 rounded flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      {userCredits.calendars_remaining === -1 ? '∞' : userCredits.calendars_remaining} calendars
                    </span>
                  ) : userCredits && userCredits.calendars_remaining > 0 ? (
                    <span className="px-2 py-0.5 bg-brand-gray-900 text-brand-gray-400 rounded border border-brand-gray-800">
                      {userCredits.calendars_remaining} calendars
                    </span>
                  ) : freeGenerationsUsed < FREE_TIER_CALENDARS ? (
                    <span className="px-2 py-0.5 bg-brand-orange/20 text-brand-orange border border-brand-orange/30 rounded flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {FREE_TIER_CALENDARS - freeGenerationsUsed} free left
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-red-900/30 text-red-400 border border-red-800/50 rounded flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      No credits
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Pricing Button */}
            {user && (
              <button 
                onClick={() => setShowPricing(true)}
                data-testid="pricing-btn"
                className="text-sm font-headline uppercase tracking-wide text-black bg-brand-orange hover:bg-brand-orange/90 flex items-center gap-2 px-4 py-1.5 rounded-full transition-colors"
              >
                <CreditCard className="w-4 h-4" /> Upgrade
              </button>
            )}

            {/* Admin Button (only for admin) */}
            {user?.email === ADMIN_EMAIL && (
              <>
                <button 
                  onClick={() => setShowAnalytics(true)}
                  data-testid="analytics-btn"
                  className="text-sm font-medium text-brand-gray-400 hover:text-white flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
                  title="Analytics Dashboard"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setShowAdmin(true)}
                  data-testid="admin-btn"
                  className="text-sm font-medium text-brand-gray-400 hover:text-white flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
                  title="Admin Dashboard"
                >
                  <Shield className="w-4 h-4" />
                </button>
              </>
            )}
            
            {/* Team Settings Button */}
            {user && (
              <button 
                onClick={() => setShowTeamSettings(true)}
                data-testid="team-btn"
                className="text-sm font-medium text-brand-gray-400 hover:text-white flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
                title="Team Settings"
              >
                <Users className="w-4 h-4" />
              </button>
            )}
            
            {/* Calendar History Button */}
            {user && (
              <button 
                onClick={() => setShowCalendarHistory(true)}
                data-testid="history-btn"
                className="text-sm font-medium text-brand-gray-400 hover:text-white flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
                title="Calendar History"
              >
                <History className="w-4 h-4" />
              </button>
            )}

            {/* Social Queue Button - Hidden since integrated into main workflow */}
            {/* {user && (
              <button 
                onClick={() => setView('social-queue')}
                data-testid="social-queue-btn"
                className={`text-sm font-medium flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                  view === 'social-queue' 
                    ? 'text-black bg-brand-orange' 
                    : 'text-brand-gray-400 hover:text-white'
                }`}
                title="Social Queue"
              >
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Queue</span>
              </button>
            )} */}

            {view === 'results' && (
              <>
                <button 
                  onClick={() => generateImagesForPosts(posts)} 
                  disabled={isBatchGenerating} 
                  data-testid="batch-generate-images-btn"
                  className="hidden sm:flex text-sm font-medium text-white bg-brand-gray-800 border border-brand-gray-700 hover:bg-brand-gray-700 hover:border-brand-orange/50 items-center gap-2 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isBatchGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Generate Images
                </button>
                <button 
                  onClick={resetApp} 
                  data-testid="new-strategy-btn"
                  className="text-sm font-medium text-brand-gray-400 hover:text-brand-orange flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> New
                </button>
              </>
            )}
            
            {user ? (
              <button 
                onClick={handleLogout} 
                data-testid="logout-btn"
                className="text-sm font-medium text-brand-gray-400 hover:text-red-400 flex items-center gap-2 px-3 py-1.5 transition-colors" 
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            ) : (
              <button 
                onClick={() => setView('auth')} 
                data-testid="login-btn"
                className="text-sm font-headline uppercase tracking-wide text-black bg-brand-orange hover:bg-brand-orange/90 px-5 py-2 rounded-full transition-colors"
              >
                Log In
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-grow p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Low Credits Alert - shown when user has ≤2 credits remaining */}
          {user && view === 'input' && (
            <LowCreditsAlert
              userCredits={userCredits}
              freeGenerationsUsed={freeGenerationsUsed}
              freeTierLimit={FREE_TIER_CALENDARS}
              onUpgrade={() => setShowPricing(true)}
            />
          )}
          
          {view === 'auth' ? (
            <div className="flex flex-col items-center justify-center min-h-[70vh]">
              <AuthForm onSuccess={() => setView('input')} />
            </div>
          ) : view === 'input' ? (
            <div className="flex flex-col items-center justify-center min-h-[80vh]">
              <InputForm 
                onSubmit={handleGenerate} 
                isLoading={isLoading}
                userCredits={userCredits}
                userTeams={userTeams}
                activeTeamId={activeTeamId}
                onTeamChange={setActiveTeamId}
                onNeedUpgrade={(feature, credits) => {
                  setPaywallFeature(feature);
                  setPaywallCredits(credits);
                  setShowPaywall(true);
                }}
              />
            </div>
          ) : view === 'social-queue' ? (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-headline uppercase tracking-wider text-white flex items-center gap-3">
                    <Send className="w-6 h-6 text-brand-orange" /> Social Queue
                  </h2>
                  <p className="text-brand-gray-400 mt-1">Create, schedule, and publish posts to your social platforms</p>
                </div>
                <button
                  onClick={() => setView('input')}
                  className="text-sm font-medium text-brand-gray-400 hover:text-brand-orange flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" /> Back to Calendar
                </button>
              </div>
              <SocialQueue />
            </div>
          ) : (
            <div className="space-y-6" data-testid="results-view">
              {/* Header with title and action buttons */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-headline uppercase tracking-wider text-white flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-brand-orange" /> Content Strategy
                  </h2>
                  <p className="text-brand-gray-400 mt-1">Campaign for <span className="text-brand-orange font-semibold">{currentProfile?.name}</span></p>
                </div>
                <button
                  onClick={() => setShowConnectPlatforms(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-brand-gray-800 hover:bg-brand-gray-700 border border-brand-gray-700 rounded-lg text-sm font-medium text-white transition-colors"
                >
                  <Link2 className="w-4 h-4" /> Connect Platforms
                </button>
              </div>

              {/* Post Status Filter Tabs */}
              <div className="flex items-center gap-2 p-1 bg-brand-gray-900 border border-brand-gray-700 rounded-xl w-fit">
                {[
                  { key: 'all', label: 'All Posts', count: postCounts.all },
                  { key: 'draft', label: 'Drafts', count: postCounts.draft },
                  { key: 'scheduled', label: 'Scheduled', count: postCounts.scheduled },
                  { key: 'posted', label: 'Published', count: postCounts.posted }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setPostFilter(tab.key)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      postFilter === tab.key
                        ? 'bg-brand-orange text-black'
                        : 'text-brand-gray-400 hover:text-white hover:bg-brand-gray-800'
                    }`}
                  >
                    {tab.label}
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      postFilter === tab.key ? 'bg-black/20' : 'bg-brand-gray-800'
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {newsSummary && (
                <div className="bg-brand-gray-900 border border-brand-orange/30 rounded-2xl p-6 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><Newspaper className="w-24 h-24 text-brand-orange" /></div>
                  <div className="relative z-10 flex flex-col md:flex-row gap-6">
                    <div className="shrink-0 flex items-center justify-center w-12 h-12 bg-brand-orange/10 text-brand-orange border border-brand-orange/30 rounded-full"><Info className="w-6 h-6" /></div>
                    <div className="space-y-3 flex-1">
                      <h3 className="text-sm font-headline uppercase tracking-widest text-brand-orange">Research Snapshot</h3>
                      <p className="text-brand-gray-300 text-base leading-relaxed">{newsSummary}</p>
                      
                      {/* Source Citations */}
                      {newsCitations && newsCitations.length > 0 && (
                        <div className="pt-3 border-t border-brand-gray-700/50">
                          <p className="text-xs text-brand-gray-500 mb-2 font-medium">Sources:</p>
                          <div className="flex flex-wrap gap-2">
                            {newsCitations.slice(0, 5).map((url, idx) => {
                              // Extract domain for display
                              let domain = '';
                              try {
                                domain = new URL(url).hostname.replace('www.', '');
                              } catch {
                                domain = `Source ${idx + 1}`;
                              }
                              return (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-brand-gray-800 hover:bg-brand-gray-700 border border-brand-gray-700 rounded text-xs text-brand-gray-400 hover:text-white transition-colors"
                                >
                                  <span className="text-brand-orange font-bold">[{idx + 1}]</span>
                                  <span className="truncate max-w-[120px]">{domain}</span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state when filter has no results */}
              {sortedDates.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-brand-gray-500">No {postFilter === 'all' ? '' : postFilter} posts found.</p>
                </div>
              )}

              <div className="space-y-12">
                {sortedDates.map(date => (
                  <div key={date} className="relative">
                    <div className="sticky top-20 z-10 mb-6 flex items-center gap-4">
                      <div className="inline-block px-4 py-1.5 rounded-full bg-brand-gray-900 border border-brand-gray-700 shadow-xl">
                        <h3 className="text-sm font-headline uppercase tracking-wider text-brand-orange">
                          {new Date(date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </h3>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {postsByDate[date].map(post => (
                        <PostCard 
                          key={post.id} 
                          post={post} 
                          onUpdate={handleUpdatePost} 
                          onPublish={handlePublishClick} 
                          profile={currentProfile}
                          userCredits={userCredits}
                          automationSettings={automationSettings}
                          connectedAccounts={connectedAccounts}
                          userId={user?.uid}
                          onBlueSkyPost={(postedPost, postUrl) => {
                            // Refresh automation settings after posting
                            loadAutomationSettings();
                          }}
                          onComposioPublish={() => {
                            // Refresh connected accounts after publishing
                            loadConnectedAccounts();
                          }}
                          onNeedCredits={(feature, credits) => {
                            setPaywallFeature(feature);
                            setPaywallCredits(credits);
                            setShowPaywall(true);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {selectedPost && (
        <PublishModal 
          post={selectedPost} 
          isOpen={isModalOpen} 
          onClose={() => {
            setIsModalOpen(false);
            // Small delay before clearing selectedPost to allow animation
            setTimeout(() => setSelectedPost(null), 200);
          }} 
          onConfirm={handleConfirmPosted} 
        />
      )}

      {/* Pricing Modal */}
      {showPricing && (
        <PricingPage onClose={() => {
          setShowPricing(false);
          loadUserCredits();
        }} />
      )}

      {/* Admin Dashboard Modal */}
      {showAdmin && (
        <AdminDashboard onClose={() => setShowAdmin(false)} />
      )}

      {/* Payment Status Pages */}
      {view === 'payment-success' && (
        <PaymentStatus onContinue={() => {
          window.history.replaceState({}, '', '/');
          setView('input');
          loadUserCredits();
        }} />
      )}
      {view === 'payment-cancel' && (
        <PaymentStatus onContinue={() => {
          window.history.replaceState({}, '', '/');
          setView('input');
        }} />
      )}

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        onUpgrade={() => {
          setShowPaywall(false);
          setShowPricing(true);
        }}
        feature={paywallFeature}
        creditsNeeded={paywallCredits}
      />

      {/* Profile Settings Modal */}
      {showProfileSettings && (
        <ProfileSettings onClose={() => setShowProfileSettings(false)} />
      )}

      {/* Legal Pages */}
      {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      {showContact && <Contact onClose={() => setShowContact(false)} />}

      {/* Analytics Dashboard (Admin only) */}
      {showAnalytics && <AnalyticsDashboard onClose={() => setShowAnalytics(false)} />}

      {/* Team Settings */}
      {showTeamSettings && <TeamSettings onClose={() => setShowTeamSettings(false)} />}

      {/* Calendar History */}
      {showCalendarHistory && (
        <CalendarHistory 
          onClose={() => setShowCalendarHistory(false)}
          onLoadCalendar={handleLoadCalendar}
        />
      )}

      {/* Team Invite */}
      {showTeamInvite && (
        <TeamInvite 
          onClose={() => {
            setShowTeamInvite(false);
            window.history.replaceState({}, '', '/');
          }}
          onNeedLogin={() => {
            setShowTeamInvite(false);
            setView('auth');
          }}
        />
      )}

      {/* Connect Platforms Modal */}
      {showConnectPlatforms && (
        <ConnectedAccounts onClose={() => setShowConnectPlatforms(false)} />
      )}

      {/* Toast Notifications */}
      <Toaster richColors position="top-right" />

      {/* Footer with Legal Links */}
      {user && view !== 'landing' && (
        <footer className="border-t border-brand-gray-800 py-4 px-6 bg-brand-gray-950">
          <div className="max-w-7xl mx-auto flex flex-col gap-4">
            {/* ArcheForge branding */}
            <div className="flex justify-center">
              <p className="text-sm font-headline tracking-wider">
                <span className="text-white">ArcheForge</span>
                <span className="text-white align-super text-[10px]">™</span>
                <span className="text-brand-gray-500"> — Built on </span>
                <a href="https://soulprintengine.ai" target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:underline">
                  SoulPrintEngine.ai
                </a>
                <span className="text-brand-orange align-super text-[10px]">™</span>
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-brand-gray-500">
              <p>© 2026 FlowGen.social by Cool Geek Media</p>
              <div className="flex items-center gap-6">
                <button
                  onClick={() => setShowPrivacy(true)}
                  className="hover:text-brand-orange transition-colors"
                  data-testid="footer-privacy-link"
                >
                  Privacy
                </button>
                <button
                  onClick={() => setShowTerms(true)}
                  className="hover:text-brand-orange transition-colors"
                  data-testid="footer-terms-link"
                >
                  Terms
                </button>
                <button
                  onClick={() => setShowContact(true)}
                  className="hover:text-brand-orange transition-colors"
                  data-testid="footer-contact-link"
                >
                  Contact
                </button>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
